// Path: ./src/app/components/CheckBox/index.tsx
import * as React from "react";
import styled from "styled-components";
import {
  Checkbox,
  FormControlLabel,
  SxProps,
  Theme,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

interface Props {
  title?: string;
  FormControlStyle?: SxProps<Theme>;
  CheckboxStyle?: React.CSSProperties;
  /**
   * Callback function to handle the click event for the CheckBoxMui component.
   * This function is invoked when the CheckBoxMui element is clicked.
   */
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
  dataTestId?: string;
  value?: boolean;
  /**
   * Optional tooltip text to display on hover
   */
  tooltip?: string;
  /**
   * Tooltip placement direction - will dynamically adjust to stay within viewport
   */
  tooltipPlacement?:
    | "bottom"
    | "left"
    | "right"
    | "top"
    | "bottom-end"
    | "bottom-start"
    | "left-end"
    | "left-start"
    | "right-end"
    | "right-start"
    | "top-end"
    | "top-start";
  /**
   * Whether tooltip should flip to opposite side when it would be cut off
   */
  tooltipFlip?: boolean;
  /**
   * Whether tooltip should follow cursor movement
   */
  tooltipFollowCursor?: boolean;
  /**
   * Whether to show an info icon next to the checkbox label when tooltip is provided
   */
  showInfoIcon?: boolean;
}

/**
 * A customizable checkbox component with integrated tooltip functionality.
 *
 * @description
 * CheckBoxMui provides a flexible checkbox with optional tooltip support that appears only when hovering over the info icon.
 * The component supports dynamic tooltip positioning, viewport boundary detection, and offers both icon-based and
 * full-component tooltip modes for maximum flexibility.
 *
 * @example
 * ```tsx
 * // Basic usage with tooltip
 * <CheckBoxMui
 *   title="Enable notifications"
 *   tooltip="Receive email notifications for important updates"
 *   value={isNotificationEnabled}
 *   onChange={(event, checked) => setNotificationEnabled(checked)}
 * />
 *
 * // Checkbox with custom styling and placement
 * <CheckBoxMui
 *   title="Accept terms"
 *   tooltip="Read our terms and conditions before proceeding"
 *   tooltipPlacement="bottom"
 *   value={termsAccepted}
 *   onChange={handleTermsChange}
 *   FormControlStyle={{
 *     "& .MuiFormControlLabel-label": {
 *       fontSize: "16px",
 *       fontWeight: "bold"
 *     }
 *   }}
 * />
 *
 * // Checkbox without info icon (tooltip appears on entire component)
 * <CheckBoxMui
 *   title="Remember me"
 *   tooltip="Keep me logged in on this device"
 *   showInfoIcon={false}
 *   value={rememberMe}
 *   onChange={handleRememberMe}
 * />
 *
 * // Simple checkbox without tooltip
 * <CheckBoxMui
 *   title="I agree"
 *   value={agreed}
 *   onChange={handleAgreement}
 * />
 * ```
 *
 * @features
 * - Material-UI checkbox integration with custom styling
 * - Icon-only tooltip trigger (tooltip appears only when hovering over info icon)
 * - Dynamic tooltip positioning with viewport boundary detection
 * - Automatic tooltip flipping to prevent cutoff
 * - Optional cursor-following tooltip mode
 * - Customizable checkbox and label styling
 * - Full accessibility support with proper ARIA attributes
 * - Flexible tooltip placement options (12 positions)
 * - Responsive design with proper touch device handling
 * - Two tooltip modes: icon-only or full-component
 */
export function CheckBoxMui({
  title,
  FormControlStyle,
  CheckboxStyle,
  onChange,
  value = false,
  dataTestId,
  tooltip,
  tooltipPlacement = "top",
  tooltipFlip = true,
  tooltipFollowCursor = false,
  showInfoIcon = true,
}: Props) {
  const checkboxElement = (
    <Wrapper data-testid="checkboxmui-test-id">
      <FormControlLabel
        sx={{
          fontSize: "12px",
          ...FormControlStyle,
        }}
        control={
          <Checkbox
            // onClick={onClick}
            onChange={onChange}
            data-testid={dataTestId}
            checked={value}
            style={{ color: "#ff8038", ...CheckboxStyle }}
          />
        }
        label={
          <LabelWrapper>
            <span>{title}</span>
            {tooltip && showInfoIcon && (
              <Tooltip
                title={tooltip}
                placement={tooltipPlacement}
                arrow
                disableInteractive={false}
                followCursor={tooltipFollowCursor}
                PopperProps={{
                  modifiers: [
                    {
                      name: "flip",
                      enabled: tooltipFlip,
                      options: {
                        fallbackPlacements: ["top", "bottom", "left", "right"],
                      },
                    },
                    {
                      name: "preventOverflow",
                      enabled: true,
                      options: {
                        boundary: "viewport",
                        padding: 8,
                      },
                    },
                    {
                      name: "offset",
                      enabled: true,
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ],
                }}
                enterDelay={1000}
                leaveDelay={200}
              >
                <InfoIcon
                  sx={{
                    fontSize: 16,
                    color: "#9ca3afab",
                    cursor: "help",
                    marginLeft: "4px",
                    verticalAlign: "middle",
                    transition: "color 0.3s ease",
                  }}
                />
              </Tooltip>
            )}
          </LabelWrapper>
        }
      />
    </Wrapper>
  );

  if (tooltip && !showInfoIcon) {
    return (
      <Tooltip
        title={tooltip}
        placement={tooltipPlacement}
        arrow
        disableInteractive={false}
        followCursor={tooltipFollowCursor}
        PopperProps={{
          modifiers: [
            {
              name: "flip",
              enabled: tooltipFlip,
              options: {
                fallbackPlacements: ["top", "bottom", "left", "right"],
              },
            },
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                boundary: "viewport",
                padding: 8,
              },
            },
            {
              name: "offset",
              enabled: true,
              options: {
                offset: [0, 8],
              },
            },
          ],
        }}
        enterDelay={1000}
        leaveDelay={200}
      >
        {checkboxElement}
      </Tooltip>
    );
  }

  return checkboxElement;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: start;
`;

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
